import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import Swal from 'sweetalert2';
import logger, { disableConsoleInProduction } from './utils/logger';

// Disable console methods in production (except error)
disableConsoleInProduction();

// Suppress WebSocket connection errors from webpack-dev-server HMR
// These errors occur when webpack-dev-server tries to connect via WebSocket
// but the connection fails (e.g., ws://localhost:443/ws)
// This is not critical - HMR will fall back to polling or work without WebSocket
if (process.env.NODE_ENV === 'development') {
  // Override console.error to filter WebSocket errors
  const originalError = console.error;
  console.error = (...args) => {
    // Convert all arguments to string for pattern matching
    const message = args.map(arg => {
      if (typeof arg === 'string') return arg;
      if (arg instanceof Error) return arg.message + ' ' + (arg.stack || '');
      if (typeof arg === 'object' && arg !== null) {
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      }
      return String(arg);
    }).join(' ');
    
    // Filter out WebSocket connection errors from webpack-dev-server
    // Check for various error patterns including the specific error format
    const isWebSocketError = (
      (message.includes('WebSocket') || message.includes('websocket') || message.includes('WebSocketClient')) &&
      (message.includes('localhost:443') || 
       message.includes('ws://localhost:443') ||
       message.includes('ws://localhost') ||
       message.includes('ERR_CONNECTION_REFUSED') ||
       message.includes('net::ERR_CONNECTION_REFUSED') ||
       message.includes('Failed to construct') ||
       message.includes('WebSocket connection') ||
       message.includes('connection establishment'))
    );
    
    if (isWebSocketError) {
      // Silently ignore WebSocket connection errors - they're not critical
      return;
    }
    originalError.apply(console, args);
  };

  // Also suppress unhandled errors related to WebSocket
  window.addEventListener('error', (event) => {
    const errorMessage = (event.message || '') + ' ' + (event.filename || '');
    if (
      (errorMessage.includes('WebSocket') || errorMessage.includes('websocket') || errorMessage.includes('WebSocketClient')) &&
      (errorMessage.includes('localhost:443') || 
       errorMessage.includes('ERR_CONNECTION_REFUSED') ||
       errorMessage.includes('net::ERR_CONNECTION_REFUSED'))
    ) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }, true);

  // Suppress unhandled promise rejections related to WebSocket
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason?.message || String(event.reason || '');
    if (
      (reason.includes('WebSocket') || reason.includes('websocket') || reason.includes('WebSocketClient')) &&
      (reason.includes('localhost:443') || 
       reason.includes('ERR_CONNECTION_REFUSED') ||
       reason.includes('net::ERR_CONNECTION_REFUSED'))
    ) {
      event.preventDefault();
      return false;
    }
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register service worker for PWA functionality and offline support
serviceWorkerRegistration.register({
  onSuccess: () => {
    logger.info('✅ PWA: App cached and ready for offline use');
  },
  onUpdate: (registration) => {
    logger.info('🔄 PWA: New version available. Please refresh.');
    // Show modern update notification
    Swal.fire({
      icon: 'info',
      title: 'Update Available',
      text: 'A new version of the app is available. Would you like to update now?',
      showCancelButton: true,
      confirmButtonColor: '#059669',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Update Now',
      cancelButtonText: 'Later'
    }).then((result) => {
      if (result.isConfirmed) {
        if (registration && registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        window.location.reload();
      }
    });
  }
});
